import React from 'react';
import { isMobile } from 'react-device-detect';

import BottomSheetPopup from '@/components/Common/Popup/BottomSheetPopup';
import { ChatOutlineIcon, KakaoTalkIcon, LinkColorIcon } from '@/components/Icon';
import { colors } from '@/const/colors';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { formatPrice } from '@/utils/common';
import { DEFAULT_SHARE_IMAGE_URL, copyToClipboard, openSmsApp, shareToKakaoTalk } from '@/utils/share';

interface ProductShareBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  truckNumber?: string;
  truckName?: string;
  price?: number | null;
  imageUrl?: string;
}

const ProductShareBottomSheet = ({
  isOpen,
  onClose,
  shareUrl,
  truckNumber,
  truckName,
  price,
  imageUrl,
}: ProductShareBottomSheetProps) => {
  const { showToast } = useToastContext();

  const shareTitle = [truckNumber, truckName].filter(Boolean).join(' ');

  const onClickCopyLink = async () => {
    const isCopied = await copyToClipboard(shareUrl);

    if (!isCopied) {
      showToast('링크 복사에 실패했어요.', 'error', 'bottom');
      return;
    }

    showToast('링크가 복사되었어요.', 'success', 'bottom');
    onClose();
  };

  const onClickKakaoShare = () => {
    const isShared = shareToKakaoTalk({
      title: shareTitle,
      description: price ? formatPrice(String(price)) : '직트럭에서 차량 정보를 확인해 보세요.',
      imageUrl: imageUrl || DEFAULT_SHARE_IMAGE_URL,
      link: shareUrl,
    });

    if (!isShared) {
      showToast('카카오톡 공유를 사용할 수 없어요.', 'error', 'bottom');
      return;
    }

    onClose();
  };

  const onClickSmsShare = () => {
    openSmsApp(`${shareTitle}\n${shareUrl}`);
    onClose();
  };

  const copyLinkAction = {
    label: '링크 복사',
    bgColor: 'bg-gray-2',
    icon: <LinkColorIcon width={26} height={26} />,
    onClick: onClickCopyLink,
  };

  // 카카오톡 공유, 문자 보내기는 모바일에서만 정상 동작하므로 PC 웹에서는 링크 복사만 노출한다.
  const shareActions = isMobile
    ? [
        copyLinkAction,
        {
          label: '카카오톡',
          bgColor: 'bg-kakao',
          icon: <KakaoTalkIcon color={colors.grey[11]} width={26} height={26} />,
          onClick: onClickKakaoShare,
        },
        {
          label: '문자 보내기',
          bgColor: 'bg-gray-2',
          icon: <ChatOutlineIcon color={colors.gray[9]} width={26} height={26} />,
          onClick: onClickSmsShare,
        },
      ]
    : [copyLinkAction];

  return (
    <BottomSheetPopup
      isOpen={isOpen}
      onClose={onClose}
      isXMarkVisible={false}
      iconTitle={<div className="w-10 h-1 mx-auto rounded-full bg-gray-4" />}
    >
      <div className="px-4 pt-1 pb-6">
        <h4 className="text-gray-7 text-[15px] leading-[22px]">공유하기</h4>
        <p className="mt-1 font-bold text-gray-9 text-[19px] leading-[26px] sm280:text-[17px]">{truckNumber}</p>
        {truckName && <p className="mt-1 break-keep text-gray-6 text-[14px] leading-[20px]">{truckName}</p>}

        <ul className="flex items-start gap-4 mt-5">
          {shareActions.map((action) => (
            <li key={action.label}>
              <button type="button" className="flex flex-col items-center gap-2" onClick={action.onClick}>
                <span className={`flex items-center justify-center w-14 h-14 rounded-full ${action.bgColor}`}>
                  {action.icon}
                </span>
                <span className="whitespace-nowrap text-gray-8 text-[13px] leading-[18px]">{action.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        className="w-full py-4 text-center border-t border-gray-3 text-gray-7 text-[16px] leading-[22px]"
        onClick={onClose}
      >
        취소
      </button>
    </BottomSheetPopup>
  );
};

export default ProductShareBottomSheet;
